// Délégations : liste (toutes pour qui voit les utilisateurs, sinon les siennes), création,
// révocation motivée, délégations arrivant à expiration.
import { Bandeau, Bouton, ConfirmationIntegree, EtatVide, Filtres, Panneau, PuceEtat, Registre, Tableau, ZoneTexte, useFiltresPersistes, type ColonneTableau } from '@eec/ui';
import { Handshake, Plus } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../api/client';
import { nomAppartenance, useDelegations, useUtilisateurs } from '../../api/requetes';
import type { Delegation } from '../../api/types';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { LIBELLES_DOMAINES, peut } from '../../session/permissions';
import { delegationsArrivantAExpiration, HORIZON_EXPIRATION_JOURS } from './delegations';
import { FormulaireDelegation, LIBELLES_TYPES } from './FormulaireDelegation';

const ETATS: Record<Delegation['statut'], { ton: 'ok' | 'attente' | 'neutre' | 'retard'; libelle: string }> = {
  en_vigueur: { ton: 'ok', libelle: 'En vigueur' },
  a_venir: { ton: 'attente', libelle: 'À venir' },
  expiree: { ton: 'neutre', libelle: 'Expirée' },
  revoquee: { ton: 'retard', libelle: 'Révoquée' },
};

function Revocation({ d }: { d: Delegation }) {
  const [motif, setMotif] = useState('');
  const revocation = useAction({
    executer: () => requete<Delegation>(`/delegations/${d.id}/revoquer`, { methode: 'POST', corps: { motif } }),
    succes: 'Délégation révoquée : elle ne produit plus d\'effet.',
    invalider: [['delegations'], ['contexte']],
  });
  return (
    <>
      <ConfirmationIntegree
        petit
        libelle="Révoquer"
        question="Révoquer cette délégation ? L'effet est immédiat."
        libelleConfirmer="Confirmer la révocation"
        enCours={revocation.enCours}
        confirmationDesactivee={motif.trim().length < 3}
        onConfirmer={() => revocation.lancer(undefined)}
      >
        <ZoneTexte libelle="Motif de la révocation" value={motif} onChange={(e) => setMotif(e.target.value)} aide="Trois caractères au moins, conservé au journal" required />
      </ConfirmationIntegree>
      {revocation.messageErreur ? <p role="alert">{revocation.messageErreur}</p> : null}
    </>
  );
}

export function PageDelegations() {
  const { contexte } = useSession();
  const delegations = useDelegations();
  const utilisateurs = useUtilisateurs();
  const [creation, setCreation] = useState(false);
  const [filtres, setFiltres] = useFiltresPersistes('delegations', { statut: 'tous' });
  const moi = contexte?.appartenance_active?.id;
  const administrer = peut(contexte, 'utilisateurs', 'administrer');
  const nom = (id: string) => nomAppartenance(utilisateurs.data, id, moi);

  const toutes = delegations.data ?? [];
  const filtrees = filtres.statut === 'tous' ? toutes : toutes.filter((d) => d.statut === filtres.statut);
  const expirent = delegationsArrivantAExpiration(toutes);

  const colonnes: ColonneTableau<Delegation>[] = [
    { id: 'type', entete: 'Type', cellule: (d) => LIBELLES_TYPES[d.type] ?? d.type, valeurTri: (d) => d.type, enTeteLigne: true, masquable: false },
    { id: 'delegant', entete: 'Délégant', cellule: (d) => nom(d.delegant_appartenance_id) },
    { id: 'delegataire', entete: 'Délégataire', cellule: (d) => nom(d.delegataire_appartenance_id) },
    { id: 'perimetre', entete: 'Périmètre', cellule: (d) => d.domaines.map((x) => LIBELLES_DOMAINES[x] ?? x).join(', ') },
    {
      id: 'plafond',
      entete: 'Plafond',
      numerique: true,
      cellule: (d) => (d.plafond_montant === null ? 'aucun' : `${d.plafond_montant.toLocaleString('fr-FR')} F CFA`),
      valeurTri: (d) => d.plafond_montant,
    },
    { id: 'periode', entete: 'Période', cellule: (d) => `du ${dateHeure(d.debut)} au ${dateHeure(d.fin)}`, valeurTri: (d) => d.fin },
    {
      id: 'etat',
      entete: 'État',
      valeurTri: (d) => d.statut,
      cellule: (d) => (
        <>
          <PuceEtat ton={ETATS[d.statut].ton} libelle={ETATS[d.statut].libelle} />
          {d.motif_revocation ? <div>{d.motif_revocation}</div> : null}
        </>
      ),
    },
    {
      id: 'actions',
      entete: 'Actions',
      masquable: false,
      cellule: (d) =>
        d.statut !== 'revoquee' && d.statut !== 'expiree' && (d.delegant_appartenance_id === moi || administrer) ? <Revocation d={d} /> : null,
    },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Administration"
        titre="Délégations"
        description="Une délégation transmet, pour une période et un périmètre, des pouvoirs que le délégant détient lui-même."
        actions={!creation ? <Bouton icone={Plus} onClick={() => setCreation(true)}>Nouvelle délégation</Bouton> : null}
      />
      {creation ? (
        <Panneau titre="Nouvelle délégation">
          <FormulaireDelegation onTermine={() => setCreation(false)} />
        </Panneau>
      ) : null}
      <EtatRequete chargement={delegations.isPending} erreur={delegations.error} onReessayer={() => void delegations.refetch()} fonction="La liste des délégations">
        {expirent.length > 0 ? (
          <Registre
            titre={`Arrivant à expiration (${HORIZON_EXPIRATION_JOURS} jours)`}
            compteur={`${expirent.length} délégation${expirent.length > 1 ? 's' : ''}`}
            elements={expirent.map((d) => ({
              id: d.id,
              reference: date(d.fin),
              objet: `${LIBELLES_TYPES[d.type]} de ${nom(d.delegant_appartenance_id)} à ${nom(d.delegataire_appartenance_id)}`,
              sousTitre: d.domaines.map((x) => LIBELLES_DOMAINES[x] ?? x).join(', '),
              etat: { ton: 'attente', libelle: 'Expire bientôt' },
            }))}
          />
        ) : null}
        <Filtres
          groupes={[
            {
              id: 'statut',
              libelle: 'État',
              options: [
                { valeur: 'tous', libelle: 'Toutes' },
                { valeur: 'en_vigueur', libelle: 'En vigueur' },
                { valeur: 'a_venir', libelle: 'À venir' },
                { valeur: 'expiree', libelle: 'Expirées' },
                { valeur: 'revoquee', libelle: 'Révoquées' },
              ],
            },
          ]}
          valeurs={filtres}
          onChanger={setFiltres}
        />
        {!peut(contexte, 'utilisateurs', 'voir') ? (
          <Bandeau type="info">Vous voyez les délégations que vous avez données ou reçues.</Bandeau>
        ) : null}
        {filtrees.length === 0 ? (
          <EtatVide titre="Aucune délégation" icone={Handshake}>
            <p>{toutes.length === 0 ? 'Aucune délégation n\'est enregistrée.' : 'Aucune délégation ne correspond à ce filtre.'}</p>
          </EtatVide>
        ) : (
          <Tableau legende="Délégations" colonnes={colonnes} lignes={filtrees} cleLigne={(d) => d.id} cleColonnes="delegations" />
        )}
      </EtatRequete>
    </>
  );
}
